import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { user } from 'services';
import { path } from 'router/sections';
import { getMeanType } from 'services/types';
import { Confirm, Headline, PageWrapper, CardWrapper, TableExt, CloseCardButton, Button, themes } from 'libs/ui';
import { useEscape } from 'hooks';
import { getStayInfo, removeStayInfo, changeHandicapped, closeParking } from 'store/ducks/models/stays';
import { setParameters, resetParameters } from 'store/ducks/interface/widgetPopup';
import { PromiseContract } from './promise-contract';
import { Penalty } from './penalty';
import { FreeBlock } from './FreeBlock';
import { TicketAction, AppPayAction, SetTariffAction } from './actions';
import { NewTicketAction } from 'grids/stay/actions/new-ticket-action';
import { StayCardRide } from './ride';
import { Payments } from './payments/payments';
import { getTableParams } from 'grids/stay/grid.params';
import { MeanTypeId, MeanId, AddtionalWrapper, ButtonContainer2, ClientName, FreeButton, HandicappedBlock, Hover } from './stay-card.styled';

export const StayCard = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const info = useSelector((store) => store.stays.stay);

  const [showFree, setShowFree] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [showPromise, setShowPromise] = useState(false);
  const [showPenalty, setShowPenalty] = useState(false);

  const hoverDelay = useRef(null);
  const hoverDelayClose = useRef(null);
  const hoverPlace = useRef(null);

  useEffect(() => {
    const id = getStayId();
    dispatch(removeStayInfo());
    dispatch(getStayInfo(id));

    return () => {
      clearTimeout(hoverDelay.current);
      clearTimeout(hoverDelayClose.current);
    };
  }, [dispatch]);

  const getStayId = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('id');
  };

  /**
   * close park session card
   */
  const closeCard = () => {
    dispatch(removeStayInfo());
    // console.log('🚀 ~ closeCard ~ document.referrer:', document.referrer);
    if (location.key) {
      history.goBack();
    } else {
      history.push(path('stays'));
    }
  };

  // close on escape
  useEscape({ action: closeCard });

  const openFree = () => setShowFree(true);
  const closeFree = () => setShowFree(false);

  const getRides = () => {
    const rides = info.periods || [];
    return rides.map((item, idx) => {
      const from = info.passages[item.id_1] || { data: { photos: {} } };
      const to = info.passages[item.id_2] || { data: { photos: {} } };
      const last = rides.length === idx + 1;

      return (
        <StayCardRide key={`period${item.id_1}${item.id_2}`} from={from} to={to} last={last} amount={item.amount} duration={item.duration} is_suspicious={info.is_suspicious} />
      );
    });
  };

  const mouseEnter = () => {
    clearTimeout(hoverDelayClose.current);
    hoverDelay.current = setTimeout(setWidgetParams, 500);
  };

  const mouseLeave = (e) => {
    const target = e.relatedTarget;
    hoverDelayClose.current = setTimeout(() => {
      const widget = document.getElementById('widget-popup');
      if (target !== widget) {
        clearTimeout(hoverDelay.current);
        dispatch(resetParameters());
      }
    }, 500);
  };

  const setWidgetParams = () => {
    if (hoverPlace.current) {
      const el = hoverPlace.current;
      const rect = el.getBoundingClientRect();
      // const leftShift = 250;
      const parameters = {
        content: el.innerText,
        width: 'auto',
        fontSize: '14px',
        top: rect.bottom + 15,
        left: rect.left, // - leftShift,
        parent: el,
        clearParentClose: () => clearTimeout(hoverDelayClose.current),
      };
      dispatch(setParameters(parameters));
    }
  };

  /**
   * refresh parking session
   */
  const refresh = () => {
    const id = getStayId();
    dispatch(getStayInfo(id));
  };

  const confirmCloseParking = () => setConfirm(true);

  // promise
  const promiseForm = () => setShowPromise(true);
  const hidePromiseForm = () => {
    refresh();
    setShowPromise(false);
  };

  // penalty
  const penaltyForm = () => setShowPenalty(true);
  const hidePenaltyForm = () => {
    setShowPenalty(false);
    refresh();
  };

  const second = info && info.is_secondentry;
  const tableParams = getTableParams(false, t);

  return (
    <PageWrapper>
      <Headline>
        <p>{t('cards.Карточка парковки')}</p>
        <CloseCardButton close={closeCard} />
      </Headline>
      {info && (
        <>
          <TableExt rows={{ count: 1, items: [info] }} {...tableParams} />
          <CardWrapper>
            <MeanTypeId>
              {info.mean_type && (
                <ClientName>{`${getMeanType(info.mean_type)} 
              ${info.mean_number || ''}`}</ClientName>
              )}
              {info.mean_type === 'receipt' && <MeanId>ID {info.mean_id}</MeanId>}
            </MeanTypeId>

            {getRides()}

            <AddtionalWrapper>
              <ButtonContainer2 space="20px">
                <TicketAction />
                <NewTicketAction refresh={refresh} />
                <AppPayAction />

                {info.mean_id &&
                  info.mean_type &&
                  (info.is_handicapped ? (
                    <HandicappedBlock className="free" ref={hoverPlace} onMouseEnter={mouseEnter} onMouseLeave={mouseLeave}>
                      <img src="/images/tax-free.png" alt="" />
                      <Hover>
                        {t('form.Льгота')}
                        <br />
                        {info.handicapped_permit_number}
                      </Hover>
                    </HandicappedBlock>
                  ) : (
                    info.is_active &&
                    user.haveRight('stays.set_exemption') && (
                      <FreeButton theme={themes.gray} fontSize="16px" onClick={openFree}>
                        <img src="/images/tax-free.png" alt="" />
                        {t('form.Льгота')}
                      </FreeButton>
                    )
                  ))}

                <SetTariffAction />

                {info.is_active && user.haveRight('stays.manual_close') && (
                  <Button theme={themes.red} fontSize="16px" onClick={confirmCloseParking}>
                    {t('buttons.Закрыть парковку')}
                  </Button>
                )}
                {second && (
                  <Button title={t('grid.Повторный проезд')} theme={themes.gray} disabled>
                    <img src="/images/second.png" height="20" alt="se" />
                  </Button>
                )}
                {info.is_active && user.haveRight('promise_pay.edit') && (
                  <Button title={t('sections.Договор паркирования')} theme={themes.gray} onClick={promiseForm}>
                    {t('sections.Договор паркирования')}
                  </Button>
                )}
                {info.is_active && user.haveRight('stays.add_penalty') && (
                  <Button title={t('buttons.Добавить услугу')} theme={themes.gray} onClick={penaltyForm}>
                    {t('buttons.Добавить услугу')}
                  </Button>
                )}
              </ButtonContainer2>

              <Payments info={info} />
            </AddtionalWrapper>

            {showPromise && <PromiseContract id={info.id} parkingId={info.parking_id} close={hidePromiseForm} />}
            {showPenalty && <Penalty id={info.id} close={hidePenaltyForm} />}
            {showFree && <FreeBlock t={t} setFree={(number) => dispatch(changeHandicapped(getStayId(), number))} close={closeFree} />}
          </CardWrapper>
        </>
      )}

      {confirm && (
        <Confirm
          width={'480px'}
          title={t('form.Закрыть парковку?')}
          type="danger"
          cancelButton={{
            text: t('buttons.Отменить'),
            func: () => {
              setConfirm(false);
            },
          }}
          acceptButton={{
            text: t('grid.Подтвердить'),
            func: () => {
              setConfirm(false);
              dispatch(
                closeParking(info.id, () => {
                  dispatch(getStayInfo(info.id));
                }),
              );
            },
            theme: themes.blue,
          }}
        />
      )}
    </PageWrapper>
  );
};
